import { MissionAssessmentRequestStatus } from '@@types/api.types';
import { MainLayout } from '@components/layouts/main.layout';
import { HomeOnboardingTile } from '@components/pages/home/components/home-onboarding-tile.component';
import { HelmetWrapper } from '@components/shared/helmet.component';
import { PageviewTracker } from '@components/shared/pageview-tracker.component';
import { useGetMissionAssessmentDraftsForExpert } from '@queries/mission-assessment-drafts.query';
import { useExpertGetMissionAssessmentRequests } from '@queries/mission-assessment-requests.query';
import { useMissionAssessmentAllQueryOptions } from '@queries/mission-assessments.query';
import { useGetCurrentUser } from '@queries/users.query';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import {
  MissionAssessmentList,
  calculateAggregatedCompletedAssessments,
  deduplicateDraftsByRequest,
} from './components/mission-assessment-list.component';

export const MissionAssessmentsPage: React.FC = () => {
  const [exactMatchOnly, setExactMatchOnly] = React.useState(false);

  const { data: user } = useGetCurrentUser();
  const userHasValidAccessAndExpertProfile = Boolean(user?.hasValidAccess && user?.expertProfile);

  const { data: missionAssessmentRequests = [], isLoading: isLoadingRequests } =
    useExpertGetMissionAssessmentRequests();
  const allMissionAssessmentsQueryOptions = useMissionAssessmentAllQueryOptions();
  const { data: expertAssessments = [], isLoading: isLoadingExpertAssessments } = useQuery(
    allMissionAssessmentsQueryOptions,
  );
  const { data: draftAssessments = [], isLoading: isLoadingMissionAssessmentDrafts } =
    useGetMissionAssessmentDraftsForExpert();

  const aggregatedCompletedAssessments = React.useMemo(() => {
    return calculateAggregatedCompletedAssessments(expertAssessments);
  }, [expertAssessments]);

  const deduplicatedDraftAssessments = React.useMemo(() => {
    return deduplicateDraftsByRequest(draftAssessments);
  }, [draftAssessments]);

  const draftRequestIds = React.useMemo(() => {
    return new Set(deduplicatedDraftAssessments.map((draft) => draft.request?.id).filter(Boolean));
  }, [deduplicatedDraftAssessments]);

  const availableBackendRequests = React.useMemo(() => {
    return missionAssessmentRequests.filter(
      (request) => request.status === MissionAssessmentRequestStatus.REQUESTED && !draftRequestIds.has(request.id),
    );
  }, [missionAssessmentRequests, draftRequestIds]);

  const filteredRequests = React.useMemo(() => {
    if (!exactMatchOnly || !user?.expertProfile) {
      return availableBackendRequests;
    }

    const expertCountryOfService = user.expertProfile.countryOfService;
    const expertServiceId = user.expertProfile.service?.id;

    return availableBackendRequests.filter((request) => {
      const hasCountriesOfServiceCriteria =
        request.veteranCountriesOfService && request.veteranCountriesOfService.length > 0;
      const hasServicesCriteria = request.veteranServices && request.veteranServices.length > 0;

      if (!hasCountriesOfServiceCriteria && !hasServicesCriteria) {
        return true;
      }

      const matchesCountry = hasCountriesOfServiceCriteria
        ? expertCountryOfService &&
          request.veteranCountriesOfService &&
          request.veteranCountriesOfService.includes(expertCountryOfService)
        : true;

      const matchesService = hasServicesCriteria
        ? expertServiceId && request.veteranServices && request.veteranServices.some((s) => s.id === expertServiceId)
        : true;

      return matchesCountry && matchesService;
    });
  }, [availableBackendRequests, exactMatchOnly, user?.expertProfile]);

  const hasBackendRequests = availableBackendRequests.length > 0;

  return (
    <PageviewTracker>
      <HelmetWrapper title="Mission Assessments" />
      <MainLayout
        aside={
          userHasValidAccessAndExpertProfile ? (
            <div className="w-full">
              <MissionAssessmentList
                availableRequests={[]}
                completedAssessments={aggregatedCompletedAssessments}
                draftAssessments={[]}
                exactMatchOnly={exactMatchOnly}
                hasBackendRequests={false}
                isLoadingCompleted={isLoadingExpertAssessments}
                isLoadingDrafts={false}
                isLoadingRequests={false}
                layout="compact"
                onExactMatchChange={setExactMatchOnly}
                sections={['completed']}
                userProfile={user?.expertProfile}
              />
            </div>
          ) : undefined
        }
        columnsStartAtContainer="64rem"
      >
        {userHasValidAccessAndExpertProfile ? (
          <MissionAssessmentList
            availableRequests={filteredRequests}
            completedAssessments={[]}
            draftAssessments={deduplicatedDraftAssessments}
            exactMatchOnly={exactMatchOnly}
            hasBackendRequests={hasBackendRequests}
            isLoadingCompleted={false}
            isLoadingDrafts={isLoadingMissionAssessmentDrafts}
            isLoadingRequests={isLoadingRequests}
            onExactMatchChange={setExactMatchOnly}
            sections={['available', 'draft']}
            userProfile={user?.expertProfile}
          />
        ) : (
          <HomeOnboardingTile />
        )}
      </MainLayout>
    </PageviewTracker>
  );
};
